import React from 'react';

import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useVariantFromUrl } from '@jetshop/core/hooks/useProductVariants/useVariantFromUrl';
import { useIntl } from '@jetshop/intl';
import getParentOrCanonicalRoute from '@jetshop/ui/Breadcrumbs/getParentsOrCanonicalRoute';
import { Above } from '@jetshop/ui/Breakpoints';
import { Price } from '@jetshop/ui/Price';
import loadable from '@loadable/component';
import { css } from 'linaria';
import { styled } from 'linaria/react';

import { MaxWidthNoFlex } from '../Layout/MaxWidth';
import { theme } from '../Theme';
import BreadCrumbs from '../ui/BreadCrumbs.js';
import Spinner from '../ui/Spinner';
import AddToCartForm from './AddToCart/AddToCartForm';
import ImageContainer from './Images/ImageContainer';
import IntelligentDesign from './IntelligentDesign/IntelligentDesign';
import ProductInfo, { ProductSection } from './ProductInfo';
import ProductInfoAccordion from './ProductInfoAccordion';
import ProductPageLoadingState from './ProductPageLoadingState';
import { RelatedProducts } from './RelatedProducts';

import { ReactComponent as DeliveryCheck } from '../../svg/DeliveryCheck.svg';

const LoadablePackageProduct = loadable(
   () => import('./PackageProduct/PackageProduct'),
   {
      fallback: <Spinner />,
   },
);

const ProductContainer = styled(MaxWidthNoFlex)`
   padding-top: 20px;
   header {
      margin-bottom: 24px;

      h1 {
         font-size: var(--h2);
         font-family: var(--text-font);
      }

      .artikelnummer {
         font-size: var(--text-size);
         margin: 0.75em 0 0.3755em;
         display: block;
      }
   }

   .short-description {
      margin-bottom: var(--text-line-height);

      p {
         margin-bottom: var(--text-line-height);
      }
   }

   /** Quantity input */
   .quantity-input-wrapper {
      margin-bottom: 0rem;
      margin-right: 10px;
      width: 54px;

      input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button {
         -webkit-appearance: none;
         margin: 0;
      }

      input {
         width: 49px;
         height: 49px;
         text-align: center;
         padding: 0px;
         margin-bottom: 0px;
      }

      label {
         text-align: center;
      }
   }

   .delivery-list {
      li {
         display: flex;
         align-items: center;
         svg {
            margin-right: 16px;
         }
         margin-bottom: 16px;
      }
   }

   .product-details {
      ${theme.above.md} {
         align-items: flex-start;
      }

      .price,
      .new-price,
      .old-price {
         font-weight: 600;
         display: inline-block;
      }
      .price,
      .new-price {
         font-size: 1.5rem;
      }
      .new-price {
         margin-right: 8px;
         color: ${theme.colors.red};
      }
      .old-price {
         color: #707070;
      }
   }
`;

const Grid = styled.section`
   position: relative;
   display: grid;
   grid-column-gap: 24px;
   width: 1340px;
   max-width: 100%;

   @media (min-width: ${theme.breakpoints.md}) {
      grid-template-columns: 15fr 12fr;
      grid-column-gap: 42px;
   }
`;

const symetricGrid = css`
   @media (min-width: ${theme.breakpoints.md}) {
      grid-template-columns: repeat(2, minmax(auto, 575px));
      justify-content: space-between;
   }
`;

const HiddenCartButtonText = styled('p')`
   margin: var(--spacing-s) 0;
   padding: 15px 30px;
   background-color: var(--beige-2);
   width: fit-content;
   color: var(--vit);
   font-weight: 800;
`;

const price = css`
   .new-price {
      color: var(--red) !important;
   }
`;

const Product = ({ result: { data, loading }, product }) => {
   const initialVariant = useVariantFromUrl();
   const t = useIntl();

   const variantHandler = useProductVariants(product, {
      initialVariant,
      preselectCheapest: true,
   });

   const {
      selectedVariant: selectedVariation,
      getMissingOptions,
   } = variantHandler;

   // Return early if there is no product
   if (!product)
      return (
         <ProductContainer>
            <ProductPageLoadingState />
         </ProductContainer>
      );

   const getCustomField = id => {
      return product?.customFields?.find(c => c.key === id);
   };

   const images = product.images ?? [];
   const parents = getParentOrCanonicalRoute(data?.route?.parents, product);
   const relatedProducts = product.relatedProducts;
   const intelligentDesignCatId = getCustomField('intelligentDesignCatId')
      ?.stringValue;

   const hideAddToCart = getCustomField('hideAddToCart')?.boolValue;

   return (
      <ProductContainer>
         <BreadCrumbs
            parents={parents}
            breadcrumbText={product.breadcrumbText}
         />

         <Grid>
            <ProductSection>
               {images && (
                  <ImageContainer
                     images={images}
                     variant={selectedVariation}
                     badges={product.badges}
                     product={product}
                  />
               )}
            </ProductSection>
            <ProductSection>
               <div className="product-details">
                  <header>
                     <h1 data-testid="product-title">
                        {product.name || 'Loading...'}
                     </h1>
                     <span className="artikelnummer">
                        Art.nr {product.articleNumber}
                     </span>
                     <h3>
                        <Price
                           price={product.price}
                           previousPrice={product.previousPrice}
                           className={price}
                        />
                     </h3>
                  </header>
                  <div
                     className="short-description"
                     dangerouslySetInnerHTML={{
                        __html: product.shortDescription,
                     }}
                  />
                  {product?.isPackage && (
                     <LoadablePackageProduct product={product} />
                  )}
                  {!hideAddToCart && !product?.isPackage && (
                     <>
                        <AddToCartForm
                           product={product}
                           variant={selectedVariation}
                           getMissingOptions={getMissingOptions}
                           variantHandler={variantHandler}
                        />
                        <ul
                           className="delivery-list"
                           css={{ marginTop: '36px' }}
                        >
                           <li>
                              <DeliveryCheck /> {t('Leverans 2-4 dagar')}
                           </li>
                           <li>
                              <DeliveryCheck /> {t('Frakt 49 kr')}
                           </li>
                           <li>
                              <DeliveryCheck /> {t('Personlig kundservice')}
                           </li>
                        </ul>
                     </>
                  )}
                  {hideAddToCart && !product?.isPackage && (
                     <HiddenCartButtonText>
                        Denna produkt går just nu endast att handla via våra
                        säljare.
                     </HiddenCartButtonText>
                  )}
               </div>
            </ProductSection>
         </Grid>
         <Grid className={symetricGrid}>
            <Above breakpoint="md">
               {matches =>
                  matches ? (
                     <ProductInfo product={product} />
                  ) : (
                     <ProductInfoAccordion product={product} />
                  )
               }
            </Above>
         </Grid>
         {intelligentDesignCatId && (
            <IntelligentDesign categoryId={intelligentDesignCatId} />
         )}
         {relatedProducts && (
            <RelatedProducts {...{ relatedProducts, loading }} />
         )}
      </ProductContainer>
   );
};

export default Product;
